import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errorMessage } from "../api/client";

export type ApiKey = { id: string; name: string; service_account: string | null; display_prefix: string; is_personal: boolean; scopes: string[]; site: string | null; expires_at: string | null; last_used_at: string | null; revoked_at: string | null; is_valid: boolean; created_at: string; key?: string };
export type ServiceAccount = { id: string; name: string; description: string; is_active: boolean; roles: string[]; key_count: number; created_at: string };

async function unwrap<T>(p: Promise<{ data?: unknown; error?: unknown }>): Promise<T> {
  const { data, error } = await p;
  if (error) throw new Error(errorMessage(error));
  return data as T;
}

export function useApiKeys() {
  return useQuery({ queryKey: ["api-keys"], queryFn: async () => (await unwrap<{ results: ApiKey[] }>(api.GET("/api/v1/accounts/api-keys/", { params: { query: { page_size: 200 } } }))).results });
}
export function useServiceAccounts() {
  return useQuery({ queryKey: ["service-accounts"], queryFn: async () => (await unwrap<{ results: ServiceAccount[] }>(api.GET("/api/v1/accounts/service-accounts/", { params: { query: { page_size: 200 } } }))).results });
}
export function useCreateKey() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body: { name: string; service_account?: string | null; scopes: string[]; expires_at?: string | null }) => unwrap<ApiKey>(api.POST("/api/v1/accounts/api-keys/", { body: body as never })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["api-keys"] }) });
}
export function useKeyAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: "revoke" | "rotate" }) => {
      if (action === "revoke") { await unwrap(api.DELETE("/api/v1/accounts/api-keys/{id}/", { params: { path: { id } } })); return null; }
      return unwrap<ApiKey>(api.POST("/api/v1/accounts/api-keys/{id}/rotate/", { params: { path: { id } } }));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}
export function useSaveServiceAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<ServiceAccount> & { id?: string }) => id
      ? unwrap<ServiceAccount>(api.PATCH("/api/v1/accounts/service-accounts/{id}/", { params: { path: { id } }, body: body as never }))
      : unwrap<ServiceAccount>(api.POST("/api/v1/accounts/service-accounts/", { body: body as never })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["service-accounts"] }),
  });
}
